import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaChartPie,
  FaChartLine,
  FaDollarSign,
  FaUsers,
  FaMapMarkerAlt,
  FaTruck,
  FaClipboard,
  FaClipboardList,
  FaUserCircle,
  FaRegUserCircle,
  FaSignOutAlt,
  FaProductHunt,
  FaGasPump,
  FaFileImport,
} from 'react-icons/fa';
import {
  FaTruckRampBox,
  FaTruckFront,
  FaBuilding,
  FaBuildingUser,
  FaHandHoldingDollar,
  FaMagnifyingGlassChart,
  FaMapLocationDot,
} from 'react-icons/fa6';
import { MdPerson } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import * as colors from '../../config/colors';
import { Title } from '../../styles/GlobalStyles';
import * as actions from '../../store/modules/auth/actions';
import Routes from '../../routes';

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: colors.primaryColor,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    background: colors.primaryColor,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const linksHome = [
  {
    to: '/home',
    icon: <FaHome size={24} style={{ color: colors.primaryColor }} />,
    text: 'Home',
  },
];

const linksLancamentos = [
  {
    to: '/viagens',
    icon: <FaTruck size={24} style={{ color: colors.primaryColor }} />,
    text: 'Controle Viagens',
  },
  {
    to: '/controledespesas',
    icon: <FaDollarSign size={24} style={{ color: colors.primaryColor }} />,
    text: 'Controle Despesa',
  },
];

const linksCadastros = [
  {
    to: '/empresas',
    icon: <FaBuilding size={24} style={{ color: colors.primaryColor }} />,
    text: 'Empresas',
  },
  {
    to: '/clientes',
    icon: <FaBuildingUser size={24} style={{ color: colors.primaryColor }} />,
    text: 'Clientes',
  },
  {
    to: '/motoristas',
    icon: <FaUsers size={24} style={{ color: colors.primaryColor }} />,
    text: 'Motoristas',
  },
  {
    to: '/postos',
    icon: <FaGasPump size={24} style={{ color: colors.primaryColor }} />,
    text: 'Postos',
  },
  {
    to: '/favorecidos',
    icon: (
      <FaHandHoldingDollar size={24} style={{ color: colors.primaryColor }} />
    ),
    text: 'Favorecidos',
  },
  {
    to: '/locais',
    icon: <FaMapMarkerAlt size={24} style={{ color: colors.primaryColor }} />,
    text: 'Locais',
  },
  {
    to: '/veiculos',
    icon: <FaTruckFront size={22} style={{ color: colors.primaryColor }} />,
    text: 'Veículos',
  },
  {
    to: '/carretas',
    icon: <FaTruckRampBox size={24} style={{ color: colors.primaryColor }} />,
    text: 'Carretas',
  },
  {
    to: '/funcionarios',
    icon: <MdPerson size={24} style={{ color: colors.primaryColor }} />,
    text: 'Funcionários',
  },
  {
    to: '/tipodespesas',
    icon: <FaClipboard size={24} style={{ color: colors.primaryColor }} />,
    text: 'Tipo Despesas',
  },
  {
    to: '/detalhes',
    icon: <FaClipboardList size={24} style={{ color: colors.primaryColor }} />,
    text: 'Detalhes',
  },
];

const linksIntegracao = [
  {
    to: '/integration',
    icon: <FaFileImport size={24} style={{ color: colors.primaryColor }} />,
    text: 'Integracao',
  },
];

const linksAdmin = [
  {
    to: '/users',
    icon: <FaUserCircle size={24} style={{ color: colors.primaryColor }} />,
    text: 'Usuários (Admin)',
  },
];

const linksEstrategico = [
  {
    to: '/dashboard',
    icon: <FaChartPie size={24} style={{ color: colors.primaryColor }} />,
    text: 'Gráficos',
  },
  {
    to: '/dashestrategico',
    icon: <FaChartLine size={24} style={{ color: colors.primaryColor }} />,
    text: 'Painéis',
  },
  {
    to: '/dashmapaproducao',
    icon: <FaProductHunt size={24} style={{ color: colors.primaryColor }} />,
    text: 'Mapa Produção',
  },
  {
    to: '/dashdre',
    icon: (
      <FaMagnifyingGlassChart
        size={24}
        style={{ color: colors.primaryColor }}
      />
    ),
    text: 'DRE',
  },
  {
    to: '/ranking',
    icon: <FaMapLocationDot size={24} style={{ color: colors.primaryColor }} />,
    text: 'Preço Diesel',
  },
];

export default function MiniDrawer() {
  const dispatch = useDispatch();
  const nomeStored = useSelector((state) => state.auth.user.nome);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isAdmin = useSelector((state) => state.auth.user.flag_admin);
  const navigate = useNavigate();

  const handleLogout = (e) => {
    e.preventDefault();
    dispatch(actions.loginFailure());
    navigate('/');
  };

  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          {(isAdmin === 0 || isAdmin === 1 || isAdmin === 2) && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 5,
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Link to="/register">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                }}
              >
                {isLoggedIn && (
                  <>
                    <FaRegUserCircle size={24} title="Conta do Usuário" />
                    <span style={{ marginLeft: '1.0rem' }}>
                      Usuário: {nomeStored}
                    </span>
                  </>
                )}
              </Box>
            </Link>
            <Box>
              {isLoggedIn && (
                <Link onClick={handleLogout} to="/logout">
                  <FaSignOutAlt
                    size={24}
                    color={colors.errorColor}
                    title="LogOut"
                  />
                </Link>
              )}
            </Box>
          </Typography>
        </Toolbar>
      </AppBar>
      {(isAdmin === 0 || isAdmin === 1 || isAdmin === 2) && (
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <Title>Menu</Title>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'rtl' ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </DrawerHeader>
          {(isAdmin === 0 || isAdmin === 1 || isAdmin === 2) && (
            <List>
              {linksHome.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}

          {(isAdmin === 0 || isAdmin === 1 || isAdmin === 2) && (
            <List>
              {linksLancamentos.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}

          {(isAdmin === 0 || isAdmin === 1) && (
            <List>
              {linksEstrategico.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}

          {(isAdmin === 0 || isAdmin === 1) && (
            <List>
              {linksCadastros.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}

          {(isAdmin === 0 ||
            isAdmin === 1 ||
            isAdmin === 2 ||
            isAdmin === 4) && (
            <List>
              {linksIntegracao.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}

          {isAdmin === 0 && (
            <List>
              {linksAdmin.map(({ to, icon, text }) => (
                <ListItem
                  key={to}
                  disablePadding
                  sx={{ display: 'block' }}
                  component={Link}
                  to={to}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider />
            </List>
          )}
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
        <DrawerHeader />
        <Routes />
      </Box>
    </Box>
  );
}
